// Apolo IDE — SelfRepairPanel (Self-Repair Loop, E4 / WS 4.2).
//
// The live view of the post-turn self-repair state machine. Renders the five
// visible stages as a left-to-right node strip —
//   CHECKER → FIXER → DOUBLECHECKER → TROUBLESHOOTER → CONSULTANT —
// highlighting the one the run is currently in, the blocked-cycle count, and,
// once the run ESCALATES, the structured report (which stages were tried + the
// recommendation). When verify passed it shows a quiet "assurance" confirmation.
//
// HOUSE STYLE (Pedro): neutral hover (background tint + a tiny slide, NEVER a
// per-item colored border — those read as errors with macOS Increase Contrast
// on); color lands ONLY on the live node. No emoji. Every node + control has an
// a11y name. Idle/minimal when nothing has run (state PROPOSED/undefined).

import { CheckCircle2, ListChecks, Loader2, ShieldAlert } from 'lucide-react';
import { STATES } from '../../lib/selfRepair/selfRepairMachine.js';

// The five visible stages, in flow order. `state` is the machine state this node
// represents; `troubleshoot`-style re-entry of DOUBLECHECKER is fine — we only
// ever light the node matching the CURRENT state.
const STAGES = [
  { state: STATES.CHECKING, label: 'Checker', hint: 'Roda a verificação do projeto' },
  { state: STATES.FIXING, label: 'Fixer', hint: 'Aplica um reparo' },
  { state: STATES.DOUBLECHECKING, label: 'Doublechecker', hint: 'Re-verifica após o reparo' },
  { state: STATES.TROUBLESHOOTING, label: 'Troubleshooter', hint: 'Depuração sistemática' },
  { state: STATES.ESCALATED, label: 'Consultant', hint: 'Escalou para planejamento' },
];

// Human label per stage, for the escalate report's "tried" list (machine state
// → the visible stage name).
const STAGE_LABEL = STAGES.reduce((acc, s) => {
  acc[s.state] = s.label;
  return acc;
}, {});

function hasRun(state) {
  return Boolean(state) && state !== STATES.PROPOSED;
}

function StageNode({ stage, active, escalated }) {
  // Color ONLY on the active node. The escalate (CONSULTANT) terminal node uses
  // the warning tint when it's the live one; every other active node uses the
  // accent. Inactive nodes are neutral surface — no colored borders anywhere.
  const isEscalateActive = active && stage.state === STATES.ESCALATED;
  const base =
    'group flex min-w-0 flex-1 items-center gap-2 rounded-md border px-2.5 py-2 text-xs transition '
    + 'hover:translate-x-0.5';
  const tone = active
    ? (isEscalateActive
      ? 'border-apolo-warning/50 bg-apolo-warningBg text-apolo-warning'
      : 'border-apolo-accentBorder bg-apolo-accentMuted text-apolo-textPrimary')
    : 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textTertiary hover:bg-apolo-surface3';

  return (
    <li
      className={`${base} ${tone}`}
      aria-current={active ? 'step' : undefined}
      aria-label={`Etapa ${stage.label}${active ? ' (em andamento)' : ''}: ${stage.hint}`}
      title={stage.hint}
    >
      <span
        aria-hidden="true"
        className={
          'inline-flex h-4 w-4 shrink-0 items-center justify-center '
          + (active && !isEscalateActive ? 'text-apolo-accent' : '')
        }
      >
        {active && !escalated && stage.state !== STATES.ESCALATED ? (
          <Loader2 size={13} className="animate-spin" />
        ) : isEscalateActive ? (
          <ShieldAlert size={13} />
        ) : (
          // A plain step dot for inactive / not-yet-reached nodes.
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
        )}
      </span>
      <span className="truncate font-medium">{stage.label}</span>
    </li>
  );
}

/**
 * @param {{
 *   state?: string,            // current machine state (STATES.*)
 *   cycles?: number,           // blocked-cycle count
 *   events?: Array<object>,    // ordered transition log (optional, for callers)
 *   report?: {                 // present only when state === ESCALATED
 *     tried?: string[],
 *     lastEvidence?: string,
 *     cycles?: number,
 *     recommendation?: string,
 *   } | null,
 * }} props
 */
export default function SelfRepairPanel({ state, cycles = 0, events = [], report = null }) {
  // Idle: nothing has run yet → minimal, unobtrusive line (no machine strip).
  if (!hasRun(state)) {
    return (
      <div
        className="rounded-lg border border-apolo-borderSubtle px-3 py-2 text-xs text-apolo-textTertiary"
        style={{ background: 'var(--rx-bg, #1F1F1E)' }}
        role="status"
        aria-label="Auto-reparo ocioso"
      >
        <span className="inline-flex items-center gap-1.5">
          <ListChecks size={13} aria-hidden="true" />
          Auto-reparo ocioso
        </span>
      </div>
    );
  }

  const passed = state === STATES.PASS;
  const escalated = state === STATES.ESCALATED;
  const eventCount = Array.isArray(events) ? events.length : 0;

  return (
    <section
      className="rounded-lg border border-apolo-borderSubtle p-3"
      style={{ background: 'var(--rx-bg, #1F1F1E)' }}
      aria-label="Auto-reparo"
      data-self-repair-state={state}
      data-event-count={eventCount}
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-medium text-apolo-textPrimary">
          <ListChecks size={14} aria-hidden="true" />
          Auto-reparo
        </h3>
        <span
          className="text-[11px] tabular-nums text-apolo-textTertiary"
          aria-label={`Ciclos: ${cycles}`}
        >
          {`Ciclos: ${cycles}`}
        </span>
      </header>

      {/* The five-stage machine strip. */}
      <ol className="flex flex-wrap items-stretch gap-1.5" aria-label="Etapas do auto-reparo">
        {STAGES.map((stage) => (
          <StageNode
            key={stage.state}
            stage={stage}
            active={state === stage.state}
            escalated={escalated}
          />
        ))}
      </ol>

      {/* Terminal: verify passed → quiet assurance confirmation. */}
      {passed && (
        <div
          className="mt-2 flex items-center gap-1.5 rounded-md border border-apolo-success/40 bg-apolo-successBg px-2.5 py-1.5 text-xs text-apolo-success"
          role="status"
          aria-label="Verificação passou"
        >
          <CheckCircle2 size={13} aria-hidden="true" />
          Verificação passou
        </div>
      )}

      {/* Terminal: escalated → the honest structured report. */}
      {escalated && report && (
        <div
          className="mt-2 rounded-md border border-apolo-warning/40 bg-apolo-warningBg p-2.5 text-xs"
          role="alert"
          aria-label="Relatório de escalonamento"
        >
          <div className="mb-1 flex items-center gap-1.5 font-medium text-apolo-warning">
            <ShieldAlert size={12} aria-hidden="true" />
            Escalou para planejamento
          </div>
          {Array.isArray(report.tried) && report.tried.length > 0 && (
            <div className="mb-1 text-apolo-textSecondary">
              <span className="text-apolo-textTertiary">Tentou: </span>
              {report.tried.map((t) => STAGE_LABEL[t] || t).join(' → ')}
            </div>
          )}
          {report.recommendation && (
            <div className="text-apolo-textSecondary">
              <span className="text-apolo-textTertiary">Recomendação: </span>
              <code className="text-apolo-textPrimary">{report.recommendation}</code>
            </div>
          )}
          {report.lastEvidence && (
            <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap rounded bg-apolo-bg p-2 text-[11px] text-apolo-textSecondary">
              {report.lastEvidence}
            </pre>
          )}
        </div>
      )}
    </section>
  );
}
